import { __ } from 'i18n-for-browser'
import '../config/i18n'
import { error } from '@react/react-spectrum/Toast'
import axios from 'axios'

const request = require('request')

export function ccApiCall (method, url, options, cb) {
    try {
        request(options, (error, response, body) => {
            let json
            if (error) {
                console.log(`error: ${error}`)
            }
            if (body) {
                json = JSON.parse(body)
                if (response && response.statusCode >= 400) {
                    cb(null, json)
                } else {
                    cb(json, response)
                }
            } else {
                cb(null, `${response}, JSON.stringify(options): ${JSON.stringify(options)}`)
            }
        })
    } catch (e) {
        console.log(e)
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
        cb(null, e)
    }
};

export async function ssApiCall (options) {
    try {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('POST', options.url)
            xhr.onload = () => {
                resolve(xhr)
            }
            xhr.onerror = () => {
                resolve(xhr)
            }
            xhr.ontimeout = e => {
                console.log('timeout')
                reject(new Error('timeout'))
            }
            xhr.setRequestHeader('X-Api-Key', `${process.env.REACT_APP_CONFIG_APP_KEY}`)
            xhr.setRequestHeader('Authorization', options.headers.Authorization)
            xhr.timeout = options.timeout
            xhr.send(options.body.formData)
        })
    } catch (e) {
        console.log(e)
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
    }
};

export async function ssApiSyncCall (options) {
    const response = await fetch(options.url, {
        headers: {
            'X-Api-Key': `${process.env.REACT_APP_CONFIG_APP_KEY}`,
            Authorization: options.headers.Authorization,
            Prefer: 'respond-sync, wait=60'
        },
        body: options.body.formData,
        method: 'post'
    })

    if (!response.ok) {
        console.log(response)
        throw new Error('Failed to call Sync service')
    }

    // Temp solution for parsing CPF output
    const blob = await response.blob()
    const newResponse = new Response(blob, {
        headers: [
            [
                'Content-Type',
                response.headers.get('Content-Type').replace('multipart/mixed', 'multipart/form-data')
            ]
        ]
    })
    if (response.headers.get('Content-Type').includes('application/json')) {
        const errResponse = await newResponse.json()
        console.error(errResponse)
        throw new Error(errResponse.statuses[0].invocations[0].message)
    }
    const formData = await newResponse.formData()
    return formData
};

export async function colligoSyncCall (options, outputFormat = 'png') {
    const response = await fetch(options.url, {
        headers: {
            'X-Api-Key': `${process.env.REACT_APP_CONFIG_APP_KEY}`,
            Authorization: options.headers.Authorization,
            Accept: 'multipart/form-data'
        },
        body: options.body.formData,
        method: 'post'
    })

    if (!response.ok) {
        console.log(response)
        throw new Error('Failed to call Sync service')
    }

    if (response.headers.get('Content-Type').includes('application/json')) {
        const errResponse = await response.json()
        console.error(errResponse)
        throw new Error(errResponse.statuses[0].invocations[0].message)
    }

    // Create a cloned response with modified content-type to ensure browser can parse it
    const clonedResponse = new Response(await response.blob(), {
        headers: {
            'Content-Type': response.headers.get('Content-Type').replace('multipart/mixed', 'multipart/form-data')
        }
    })

    // Parse the multipart form data
    let formData
    try {
        formData = await clonedResponse.formData()
    } catch (error) {
        console.error('Failed to parse multipart response', error)
        throw new Error('Failed to parse multipart response')
    }

    let metadata = null
    const images = []

    // Process each entry in the form data
    for (const [name, value] of formData.entries()) {
        if (value instanceof Blob) {
            if (value.type === 'application/json') {
                // Handle JSON metadata
                try {
                    const text = await value.text()
                    metadata = JSON.parse(text)
                } catch (e) {
                    console.warn('Failed to parse metadata JSON', e)
                }
            } else if (value.type.startsWith('image/')) {
                // Handle image data
                const imageFormat = value.type.split('/')[1]

                // Clone the blob with the correct content type
                const blob = new Blob([await value.arrayBuffer()], { type: value.type })

                images.push({
                    name,
                    filename: name,
                    format: imageFormat,
                    blob,
                    contentType: value.type
                })
            }
        }
    }

    // If no images were found, try an alternative approach
    if (images.length === 0) {
        // Get the raw binary data
        const buffer = await response.arrayBuffer()
        const uint8Array = new Uint8Array(buffer)

        // Convert to text to find headers
        const decoder = new TextDecoder()
        const text = decoder.decode(uint8Array)

        // Find image data using headers
        const imageHeaderIndex = text.indexOf(`Content-Type: image/${outputFormat}`)
        if (imageHeaderIndex !== -1) {
            const imageDataStartIndex = text.indexOf('\r\n\r\n', imageHeaderIndex) + 4
            const imageDataEndIndex = text.indexOf('\r\n--', imageDataStartIndex)

            // Extract the image data
            const imageData = imageDataEndIndex !== -1
                ? uint8Array.slice(imageDataStartIndex, imageDataEndIndex)
                : uint8Array.slice(imageDataStartIndex)

            const blob = new Blob([imageData], { type: `image/${outputFormat}` })

            images.push({
                name: 'mask',
                filename: 'mask',
                format: outputFormat,
                blob,
                contentType: `image/${outputFormat}`
            })
        }
        // Find JSON metadata
        const jsonHeaderIndex = text.indexOf('Content-Type: application/json')
        if (jsonHeaderIndex !== -1) {
            const jsonDataStartIndex = text.indexOf('\r\n\r\n', jsonHeaderIndex) + 4
            const jsonDataEndIndex = text.indexOf('\r\n--', jsonDataStartIndex)
            const jsonText = text.slice(jsonDataStartIndex, jsonDataEndIndex).trim()
            try {
                metadata = JSON.parse(jsonText)
            } catch (e) {
                console.warn('Failed to parse metadata JSON', e)
            }
        }
    }

    return {
        metadata,
        images
    }
}

export async function colligoAsyncCall (options, outputFormat = 'png') {
    // Initial request to start the job
    const response = await fetch(options.url, {
        headers: {
            'X-Api-Key': `${process.env.REACT_APP_CONFIG_APP_KEY}`,
            Authorization: options.headers.Authorization,
            Accept: 'application/json'
        },
        body: options.body.formData,
        method: 'post'
    })

    if (!response.ok) {
        console.log(response)
        throw new Error('Failed to initiate async job')
    }

    // Parse the job information from the response
    const jobInfo = await response.json()

    if (!jobInfo.links?.result?.href) {
        console.error('Invalid job response', jobInfo)
        throw new Error('Invalid job response format')
    }

    // Extract the result URL for polling
    const resultUrl = `${jobInfo.links.result.href}`
    console.log('resultUrl', resultUrl)
    console.log('jobInfo', jobInfo)

    // Poll for job completion
    let jobComplete = false
    let jobResult = null
    const maxAttempts = 60 // 60 attempts with 2 second interval = 2 minutes max
    let attempts = 0

    while (!jobComplete && attempts < maxAttempts) {
        attempts++
        await sleep(2000) // 2 second interval between polls

        const statusResponse = await fetch(resultUrl, {
            headers: {
                'X-Api-Key': `${process.env.REACT_APP_CONFIG_APP_KEY}`,
                Authorization: options.headers.Authorization,
                Accept: 'multipart/form-data'
            },
            method: 'get'
        })

        // First check x-task-status header
        const taskStatus = statusResponse.headers.get('x-task-status')
        console.log('x-task-status:', taskStatus)

        if (taskStatus === 'complete') {
            // Job is complete based on x-task-status header
            jobComplete = true
            jobResult = statusResponse
            continue
        }

        // Fallback to content-type check if x-task-status doesn't indicate completion
        if (statusResponse.headers.get('Content-Type').includes('application/json')) {
            const statusBody = await statusResponse.json()
            console.log('statusBody', statusBody)
            // If the response still contains links, the job is still processing
            if (statusBody.links?.result?.href) {
                continue
            } else {
                // Job is complete, response contains the result data
                jobComplete = true
                jobResult = statusBody
            }
        } else {
            continue
        }
    }

    if (!jobComplete) {
        throw new Error('Job timed out after maximum polling attempts')
    }
    console.log('jobResult', jobResult)

    // Process the response
    const images = []
    const metadata = jobResult.metadata || null

    // Process masks array with presignedUrls
    if (jobResult.masks && Array.isArray(jobResult.masks)) {
        // Fetch images from presignedUrls
        await Promise.all(jobResult.masks.map(async (mask) => {
            if (mask.presignedUrl) {
                try {
                    // Fetch the image data from the presigned URL
                    const imageResponse = await fetch(mask.presignedUrl)
                    if (!imageResponse.ok) {
                        console.error('Failed to fetch image from presigned URL', mask.presignedUrl)
                        return
                    }

                    const imageBlob = await imageResponse.blob()
                    const imageFormat = imageBlob.type.split('/')[1] || outputFormat

                    images.push({
                        name: mask.id || 'mask',
                        filename: mask.id || 'mask',
                        format: imageFormat,
                        blob: imageBlob,
                        contentType: imageBlob.type || `image/${outputFormat}`
                    })
                } catch (error) {
                    console.error('Error fetching image from presigned URL', error)
                }
            }
        }))
    }

    return {
        metadata,
        images
    }
}

export async function apiCallSensei (options, cb) {
    try {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('POST', options.url)
            xhr.onload = () => {
                cb(xhr)
            }
            xhr.onerror = () => {
                cb(xhr)
            }
            xhr.ontimeout = e => {
                console.log('timeout')
            }
            xhr.setRequestHeader('X-Api-Key', `${process.env.REACT_APP_CONFIG_APP_KEY}`)
            xhr.setRequestHeader('Authorization', options.headers.Authorization)
            xhr.timeout = options.timeout
            xhr.send(options.body.formData)
        })
    } catch (e) {
        console.log(e)
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
        cb(null, e)
    }
};

function pollStatus (url, options) {
    return axios.get(url, options)
        .then((response) => {
            // handle success
            return response
        })
        .catch((error) => {
        // handle error
            console.log(`pollStatus - error: ${JSON.stringify(error)}`)
        })
}

function sleep (ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

export async function getSenseiStatus (statusURL, timeout, interval) {
    const options = {
        timeout: 60000,
        headers: {
            Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
            'X-api-key': `${process.env.REACT_APP_CONFIG_APP_KEY}`
        }
    }
    var endTime = Date.now() + (timeout || 60000)
    interval = interval || 1000
    const validStatus = ['200', '400', '500']
    let response = await pollStatus(statusURL, options)
    let res = (response && response.data && response.data.statuses && Array.isArray(response.data.statuses) && response.data.statuses[0].invocations) ? response.data.statuses[0].invocations[0] : response.data
    while (!res || !validStatus.includes(res.status) || Date.now() > endTime) {
        console.log(`timing out in ${Math.floor((endTime - Date.now()) / 1000)} sec...`)
        await sleep(interval)
        response = await pollStatus(statusURL, options)
        res = (response && response.data && response.data.statuses && Array.isArray(response.data.statuses) && response.data.statuses[0].invocations) ? response.data.statuses[0].invocations[0] : response.data
    }
    return res
}

export async function ccApiCallGetFile (options, cb) {
    try {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('GET', options.url)
            xhr.onload = () => {
                cb(xhr)
            }
            xhr.onerror = () => {
                error(`${__('error_msg_title')}: in ccApiCallGetFile`, {
                    timeout: 0
                })
                cb(null)
            }
            xhr.ontimeout = e => {
                console.log(`Timeout - ${e}`)
                error(`${__('error_msg_title')}: Timeout - ${e}`, {
                    timeout: 0
                })
                cb(null, e)
            }
            xhr.setRequestHeader('X-Api-Key', `${process.env.REACT_APP_CONFIG_APP_KEY}`)
            // xhr.setRequestHeader('X-Api-Key', options.headers['X-Api-Key']);
            xhr.setRequestHeader('Authorization', options.headers.Authorization)
            xhr.setRequestHeader('Content-Type', '*/*')
            xhr.setRequestHeader('Access-Control-Allow-Origin', '*/*')
            // xhr.setRequestHeader('Strict-Transport-Security', 'max-age=10368000; includeSubDomains')
            xhr.responseType = options.responseType
            xhr.timeout = options.timeout
            xhr.send({})
        })
    } catch (e) {
        console.log(e)
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
        cb(null, e)
    }
};

export async function archiveSkycityTestfiles () {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_CONFIG_ENDPOINT_CC_STORAGE}/ops?invocation_mode=sync`,
            {
                headers: {
                    'x-api-key': process.env.REACT_APP_CONFIG_APP_KEY,
                    Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
                    'Content-Type': 'application/vnd.adobe.asset-operation+json',
                    body: JSON.stringify({
                        op: 'archive',
                        target: {
                            href: '/files/_skycity_smoketest/'
                        },
                        source: {
                            href: '/archive/_skycity_smoketest'
                        }
                    })
                }
            }
        )
        return {
            response: response,
            error: false
        }
    } catch (e) {
        console.log(e)
        error(`${__('error_msg_title')}: ${e}`, {
            timeout: 0
        })
        return {
            response: null,
            error: e
        }
    }
}
